import { NgClass } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { faUserTag } from '@fortawesome/free-solid-svg-icons';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { FormMode } from 'src/app/controls/common/form-mode';
import { DefaultManagementComponent } from 'src/app/controls/forms/default-management.component';
import { HttpError } from 'src/app/core/http-error';
import { Rights } from 'src/app/models/enums/rights';
import { IRole } from 'src/app/models/interfaces/role.interface';
import { Role } from 'src/app/models/role';
import { MemoryService } from 'src/app/services/memory.service';
import { NotificationService } from 'src/app/services/notification-service';
import { RolesService } from 'src/app/services/roles.service';
import { DetailHeaderComponent } from '../../controls/detail-header/detail-header.component';
import { ListHeaderComponent } from '../../controls/list-header/list-header.component';
import { RoleDetailContext } from './role-detail-context';
import { RoleDetailComponent } from './role-detail/role-detail.component';
import { RolesListComponent } from './roles-list/roles-list.component';

enum DisplayMode {
    List,
    Form,
}

@Component({
    selector: 'app-roles',
    templateUrl: './roles.component.html',
    styleUrls: ['./roles.component.scss'],
    imports: [MatExpansionModule, NgClass, ListHeaderComponent, RolesListComponent, DetailHeaderComponent, RoleDetailComponent],
})
export class RolesComponent extends DefaultManagementComponent<IRole, Role, number> implements OnInit {
    /**
     * Liste des icônes utilisés dans le template
     */
    public faRole = faUserTag;

    /**
     * Liste des enums utilisés dans le template
     */
    public override FormMode = FormMode;
    public override DisplayMode = DisplayMode;

    /**
     * Contexte par défaut du formulaire contenant les données invariantes entre role.
     */
    public defaultContext: RoleDetailContext = new RoleDetailContext();

    /**
     * Indique si le bouton d'ajout/Edit doit apparaitre.
     */
    public displayAddbuttom = true;
    public displayEditbuttom = true;

    private SuperAdminId: number = 0;

    /**
     * Constructeur.
     * @param notificationService : Service utilisé pour gérer l'affichage des notifications.
     * @param rolesService : Service métier utilisé pour gérer les roles.
     */
    constructor(
        protected override notificationService: NotificationService,
        private rolesService: RolesService,
        private memoryService: MemoryService,
        private spinner: NgxSpinnerService
    ) {
        super(notificationService);
    }

    /**
     * Appellé lors de l'initialisation du composant.
     */
    ngOnInit() {
        this.displayAddbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.RoleCreate);
        this.displayEditbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.RoleEdit);
    }

    /**
     * Appellé lorsque l'utilisateur selectionne un role.
     * @param role role selectionné.
     */
    public onRoleSelected(role: Role) {
        this.spinner.show('main-spinner');
        this.createContext();

        this.loadRole(role.id).subscribe(
            (x) => {
                this.refresh(Role.createFormCopy(x), FormMode.Reading);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'afficher le rôle." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'icône 'Retour à la liste'.
     */
    public onBackToList() {
        this.setDisplayMode(DisplayMode.List);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Consulter' dans le menu contextuel.
     * @param role Role sélectionné.
     */
    public onRoleDisplayed(role: Role) {
        this.spinner.show('main-spinner');
        this.createContext();

        this.loadRole(role.id).subscribe(
            (x) => {
                this.refresh(Role.createFormCopy(x), FormMode.Reading);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'afficher le rôle." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Editer' dans le menu contextuel.
     * @param role Role sélectionné.
     */
    public onRoleEdited(role: Role) {
        this.spinner.show('main-spinner');
        this.createContext();

        // Cas de la mise à jour.
        this.loadRole(role.id).subscribe(
            (x) => {
                if (
                    this.memoryService.getConnectedUser().roles.findIndex((r) => r === x.id) !== -1 &&
                    this.memoryService.getConnectedUser().roles.findIndex((r) => r === this.SuperAdminId) === -1
                ) {
                    // L'utilisateur ne peut pas editer un rôle qu'il possède à moins que ce soit le superAdmin.
                    this.refresh(Role.createFormCopy(x), FormMode.Reading);
                    this.notificationService.displayWarning('Attention', 'Vous ne pouvez pas éditer un rôle qui vous est attribué.');
                } else {
                    this.refresh(Role.createFormCopy(x), FormMode.Edition);
                }
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'éditer le rôle." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton 'Créer'.
     */
    public onRoleCreated() {
        this.displayForm(Role.createFormCopy(new Role()), FormMode.Creation);
    }

    /**
     * Appellé lorsque le contenu du formulaire a été sauvegardé.
     * @param role : Valeur du role retourné par le formulaire.
     */
    public onRoleUpdated(role: Role): void {
        this.spinner.show('main-spinner');
        if (this.context.editedItemId) {
            // Cas de la mise à jour.
            this.rolesService.update(this.context.editedItemId, role).subscribe(
                (x) => {
                    this.refreshWithNotification(Role.createFormCopy(x), 'Rôle mis à jour', FormMode.Reading);
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', 'Impossible de mettre à jour le rôle.' + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        } else {
            // Cas de la création.
            this.rolesService.add(role).subscribe(
                (x) => {
                    this.refreshWithNotification(Role.createFormCopy(x), 'Rôle créé', FormMode.Reading);
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', 'Impossible de créer le rôle.' + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        }
    }

    /**
     * Appellé lorsque le contenu du formulaire a été sauvegardé.
     * @param role : Valeur du role retourné par le formulaire.
     */
    public onRoleRightsUpdated(role: Role): void {
        this.spinner.show('main-spinner');
        this.rolesService.update(this.context.editedItemId, role).subscribe(
            (x) => {
                this.refreshWithNotification(Role.createFormCopy(x), 'Droits mis à jour', FormMode.Reading);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', 'Impossible de mettre à jour les droits.' + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Supprimer' dans le menu contextuel.
     * @param role role sélectionné.
     */
    public onRoleDeleted(role: Role) {
        this.spinner.show('main-spinner');
        // Suppression effective du role.
        this.rolesService.delete(role.id).subscribe(
            (x) => {
                this.notificationService.displaySuccess(null, 'Rôle supprimé');
                this.itemsList.refresh();
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                let errorMessage = error.message;
                if (error.code == 409) {
                    errorMessage = 'Le rôle est attribué à des groupes ou utilisateurs';
                }
                this.notificationService.displayError('Erreur', `Impossible de supprimer le rôle. ${errorMessage}`);
                this.spinner.hide('main-spinner');
            }
        );
    }

    public onRolesExported() {
        this.createContext();
        this.itemsList.export('Roles.xlsx');
    }

    public onRolesImported() {
        this.itemsList.refresh();
    }

    public onRolesHelped() {
        this.itemsList.displayHelpDialog('roles');
    }

    /**
     * Appelé lors de l'import.
     * @param roleId Identifiant du role
     */
    public onRoleRightsImported(roleId: number): void {
        this.spinner.show('main-spinner');
        this.loadRole(roleId).subscribe(
            (x) => {
                this.refresh(Role.createFormCopy(x), FormMode.Reading);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'afficher le rôle." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Charge les données d'un role.
     * @param roleId : Id du role à charger.
     */
    private loadRole(roleId: number): Observable<Role> {
        return this.rolesService.get(roleId);
    }

    /**
     * Pré-rempli les champs du formulaire.
     * @param role : Valeur utilisée pour pré-remplir les champs du formulaire.
     */
    protected fillForm(role: IRole): RoleDetailContext {
        let context = this.createContext();

        context.formContext.item = role;
        context.editedItemId = role.id;

        return context;
    }

    /**
     * Créer un nouveau contexte.
     */
    protected createContext(): RoleDetailContext {
        let context = new RoleDetailContext();
        return context;
    }
}
